import type { MeResult } from '@/lib/api/apiTypes';
import type { Coords } from '@/lib/location/location';
import { formatDistanceLabel } from '@/features/events/eventFormatting';

const FALLBACK_USER_ORIGIN = {
  latitude: 29.4241,
  longitude: -98.4936,
};

export type UserOriginCoords = {
  latitude: number;
  longitude: number;
  source: 'gps' | 'profile' | 'market' | 'fallback';
  isApproximate: boolean;
};

export type SessionLike = { me: MeResult } | null | undefined;

export type LocationOriginState = {
  coords: Coords;
  isReady: boolean;
  permissionDenied: boolean;
};

function withSource(
  latitude: number,
  longitude: number,
  source: UserOriginCoords['source']
): UserOriginCoords {
  return {
    latitude,
    longitude,
    source,
    isApproximate: source !== 'gps',
  };
}

export function getUserOriginCoords(
  session: SessionLike,
  locationState: LocationOriginState
): UserOriginCoords {
  // GPS is the only precise origin; stored/profile/market fallbacks are intentionally approximate.
  if (locationState.isReady && !locationState.permissionDenied) {
    return withSource(locationState.coords.latitude, locationState.coords.longitude, 'gps');
  }

  const profile = session?.me.profile;
  if (profile?.last_latitude != null && profile.last_longitude != null) {
    return withSource(profile.last_latitude, profile.last_longitude, 'profile');
  }

  if (profile?.browse_origin) {
    return withSource(profile.browse_origin.latitude, profile.browse_origin.longitude, 'market');
  }

  return withSource(
    FALLBACK_USER_ORIGIN.latitude,
    FALLBACK_USER_ORIGIN.longitude,
    'fallback'
  );
}

export function formatApproximateDistanceLabel(
  distanceMiles: number | null | undefined,
  origin: Pick<UserOriginCoords, 'isApproximate'>
) {
  if (distanceMiles == null) {
    return '';
  }
  const label = formatDistanceLabel(distanceMiles);
  return origin.isApproximate ? `~ ${label}` : label;
}
